import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { fonts } from '@helpers/fonts';
import { Grid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import Button from '@stories/Components/Buttons/Button/Button';
import { ButtonStyles } from '@stories/Components/Buttons/Button/Button.styles';
import SelectStyles from '@stories/Components/Forms/Select/Select.styles';
import { InputStyles } from '@stories/Components/Forms/Textbox/Textbox.styles';
import Heading from '@stories/Components/Global/Typography/Heading';
import Paragraph from '@stories/Components/Global/Typography/Paragraph';
import ParagraphStyles from '@stories/Components/Global/Typography/Paragraph.styles';
import styled, { css } from 'styled-components';
const GridOverride = styled(Grid.Default) `
  padding-bottom: ${fluid(15, 30)};
`;
const BookingContainer = styled.div `
  background-color: ${brand.grey.grey96};
  margin-left: calc(-1 * var(--gridPadding));
  padding-left: var(--gridPadding);
  border-top: 4px solid ${brand.primary.alt};
  padding-top: 26px;
  padding-bottom: 26px;
  padding-right: 26px;
  height: max-content;
  max-height: 100%;
  position: sticky;
  top: 0;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    left: calc(((100vw - 1920px) / 2) * -1);
    border-top: 4px solid ${brand.primary.alt};
    top: -4px;
    width: calc((100vw - 1920px) / 2);
    height: 100%;
    background-color: ${brand.grey.grey96};
  }

  @media ${until(Device.TabletLarge)} {
    margin-right: calc(-1 * var(--gridPadding));
    padding-right: var(--gridPadding);
  }

  grid-column: 1 / -1;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 4;
  }

  @media ${from(Device.ActualDesktop)} {
    grid-column: 1 / span 3;
  }

  display: flex;
  flex-direction: column;
`;
const Title = styled(Heading) `
  color: ${brand.primary.alt};
  text-transform: unset;
  margin-bottom: ${fluid(17, 36)};
`;
const Small = styled(Paragraph) `
  margin-bottom: 34px;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const Filters = styled.div `
  display: flex;
  flex-direction: column;
  gap: 19px;
  padding-bottom: ${fluid(15, 30)};
`;
const Filter = styled.div `
  display: grid;
  grid-template-columns: 130px 1fr;

  input {
    width: 100%;
  }
`;
const Label = styled(Paragraph) `
  margin: 0;
`;
const SelectWrapper = styled.div `
  ${SelectStyles.Element} {
    min-width: 100%;
    background-color: ${brand.white};
  }
`;
const Dates = styled.div `
  display: flex;
  flex-direction: column;
`;
const CheckboxWrapper = styled.div `
  grid-column: 1 /-1;
  padding: 15px 0px 10px;
`;
const HelpContainer = styled.div `
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  background-color: ${brand.grey.grey96};
  border-top: 4px solid ${brand.primary.alt};
  padding: 15px 20px;

  a {
    width: max-content;
    max-width: 100%;
  }

  @media ${from(Device.TabletLarge)} {
    background-color: ${brand.white};
  }

  @media ${until(Device.TabletLarge)} {
    grid-column: 1 / -1;
  }
`;
const HelpTitle = styled(Paragraph) `
  ${fonts.DaxPro.Medium};
  border-bottom: 1px solid ${brand.grey.grey89};
  padding-bottom: 10px;
`;
const HelpBody = styled(Paragraph) ``;
const HelpButton = styled(Button) `
  width: max-content;
  max-width: 100%;
  min-width: 148px;
`;
const ListingContainer = styled.div `
  grid-column: 1 / -1;

  @media ${from(Device.TabletLarge)} {
    grid-column: 5 / -1;
  }

  @media ${from(Device.ActualDesktop)} {
    grid-column: 4 / -1;
  }

  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: ${fluid(14, 18)}};

  ${ButtonStyles.Text} {
    font-size: 14px;
  }
`;
const Event = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  align-items: center;
  padding: ${fluid(11, 35)} ${fluid(18, 23)};
  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
  border-radius: 8px;

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(7, 1fr);
  }
`;
const EventInfo = styled.div `
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  text-align: left;
  height: 100%;

  ${({ showOrange }) => showOrange &&
    css `
      span:last-of-type {
        color: ${brand.red.base};
      }
    `}

  span {
    width: 100%;

    &[data-date='true'] {
      display: flex;
      flex-direction: column;
      white-space: nowrap;
      gap: 3px;
    }

    &[data-heading='true'] {
      ${fonts.DaxPro.Medium};
    }
  }
`;
const EventPrice = styled.div `
  display: flex;
  flex-wrap: wrap;
  text-align: center;
  color: ${brand.accent.orange};
  gap: ${fluid(2, 5)};
  align-items: flex-end;

  @media ${from(Device.TabletLarge)} {
    align-items: center;
    flex-direction: column;
  }

  span {
    ${fonts.DaxPro.Bold};

    &:nth-child(1) {
      font-size: ${fluid(23, 28)};
    }

    &:nth-child(2) {
      font-size: ${fluid(14, 18)};
    }

    &:nth-child(3) {
      font-size: ${fluid(6, 10)};
    }
  }
`;
const AttendeesAndBookNow = styled.div `
  display: flex;
  gap: 5px;
  align-items: center;
  grid-column: span 2;

  @media ${from(Device.TabletLarge)} {
    grid-column: span 1;
  }

  button,
  a {
    display: block;
    width: 100%;
  }
`;
const Attendees = styled.input `
  width: 40px;
  height: 46px;
  border: 1px solid #b9b9b9;
  text-align: center;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  appearance: none;
  -moz-appearance: textfield;
`;
const ValidationError = styled.small `
  color: ${brand.red.base};
  margin-top: 10px;
`;
const PaginationWrapper = styled.div `
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
const ClearDateRange = styled.span `
  text-decoration: underline;
  color: ${brand.primary.alt};
  margin-top: 10px;
  cursor: pointer;
`;
const NoResultsWrapper = styled.div `
  display: flex;
  flex-direction: column;

  flex-grow: 1;
  height: 100%;
  align-items: center;
  justify-content: center;

  @media ${from(Device.TabletLarge)} {
    width: 85%;
  }

  margin: auto;
  text-align: center;

  ${ButtonStyles.Text} {
    font-size: unset;
  }
`;
const NoResults = styled(ParagraphStyles.Paragraph) `
  font-size: ${fluid(17, 25)};
  line-height: ${fluid(21, 35)};
`;
const DateInput = styled.input `
  ${InputStyles};
  width: auto !important;
`;
const DateLayout = styled.div `
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px ${fluid(10, 25)};
`;
export const BookingTableStyles = {
    BookingContainer,
    Title,
    Small,
    Filters,
    Filter,
    Label,
    SelectWrapper,
    Dates,
    CheckboxWrapper,
    HelpContainer,
    HelpTitle,
    HelpBody,
    HelpButton,
    ListingContainer,
    Event,
    EventInfo,
    EventPrice,
    AttendeesAndBookNow,
    Attendees,
    ValidationError,
    PaginationWrapper,
    GridOverride,
    ClearDateRange,
    NoResults,
    NoResultsWrapper,
    DateInput,
    DateLayout,
};
